import { lazy, Suspense, useEffect, type ComponentType } from 'react';
import { useI18n } from './lib/i18n';
import { Routes, Route, useLocation, useParams, Navigate } from 'react-router-dom';
import { useAuth, hasRole, canSeeSuperBoard, isTvUser } from './lib/auth';
import { useMyDepartmentsLoaded } from './lib/useMyDepartment';
import { trackPageview } from './lib/analytics';
import { ScrollMemory } from './lib/ScrollMemory';
import { AppShell } from './components/AppShell';
import { ShareProvider } from './components/share/ShareProvider';
import { RecProvider } from './components/rec/RecProvider';
import { Login } from './pages/Login';
import { ResetPassword } from './pages/ResetPassword';
import { PlatformLogin } from './pages/PlatformLogin';
import { PlatformDashboard } from './pages/PlatformDashboard';
import { Dashboard } from './pages/Dashboard';
import { Claims } from './pages/Claims';
import { QueuePage } from './pages/QueuePage';
import { WaitingToClear, ReadyToPay, BouncedChecks, HeldChecks } from './pages/CheckQueues';
import { Mortgage, ReviewInvoice, InvoiceTodo, WaitingClear } from './pages/sections';
import { DepartmentHome, DepartmentQueue } from './pages/Departments';
import { PublicEffectiveness } from './pages/PublicEffectiveness';
import { PublicHoUpdate } from './pages/PublicHoUpdate';
const PublicReview = lazyRetry(() => import('./pages/PublicReview').then((m) => ({ default: m.PublicReview })));
import { PublicAtrVideo } from './pages/PublicAtrVideo';
/** Landing por departamento (2026-08-09): cada usuario aterriza en el dashboard de SU depto.
 *  Sin departamento = management (definición del dueño) → /management si es manager+.
 *  Payments (o office sin depto) conserva el dashboard clásico en "/". */
function HomeRouter() {
  const { me, loading } = useAuth();
  const { depts, loaded } = useMyDepartmentsLoaded();
  if (loading || !loaded) return null; // parpadeo breve; evita redirigir con datos a medias
  // El usuario de los televisores solo tiene una pantalla.
  if (isTvUser(me)) return <Navigate to="/tv" replace />;
  const key = depts[0]?.key ?? null;
  if (key === 'intake') return <Navigate to="/dept/intake/dashboard" replace />;
  if (key === 'claims') return <Navigate to="/dept/claims/dashboard" replace />;
  if (key === 'supplements') return <Navigate to="/dept/supplements/dashboard" replace />;
  if (key === 'scheduling') return <Navigate to="/dept/scheduling/dashboard" replace />;
  // Super Board (2026-09-10): quien tiene el tablero aterriza ahí en vez del dashboard de management.
  if (!key && canSeeSuperBoard(me)) return <Navigate to="/super-board" replace />;
  if (!key && hasRole(me, 'manager')) return <Navigate to="/management" replace />;
  return <Dashboard />;
}

/** El usuario de los televisores nunca ve el shell de oficina: cualquier ruta del shell lo manda a /tv. */
function ShellOTv() {
  const { me, loading } = useAuth();
  if (!loading && isTvUser(me)) return <Navigate to="/tv" replace />;
  return <AppShell />;
}

/** Una cola del viejo /dept/estimators/queue/:status es hoy la misma cola en Supplements. */
function RedirEstimatorQueue() {
  const { status } = useParams();
  return <Navigate to={`/dept/supplements/queue/${encodeURIComponent(status ?? '')}`} replace />;
}

/** Prompt liviano cuando un chunk viejo no carga tras recargar (no crashea la app ni reporta a Sentry). */
function ChunkReloadPrompt() {
  const { t } = useI18n();
  return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
      <p style={{ fontSize: 'var(--fs-sm)' }}>{t('app.updated')}</p>
      <button className="btn" style={{ marginTop: 10 }} onClick={() => window.location.reload()}>{t('app.reload')}</button>
    </div>
  );
}

/**
 * lazy() resistente al "chunk viejo" tras un deploy. Un import dinámico puede (a) RECHAZAR
 * (ChunkLoadError / 404 del asset) o (b) RESOLVER a un módulo inválido (undefined / sin
 * `.default`) → React.lazy tira "Cannot read properties of undefined (reading 'default'/'X')".
 * Cubrimos AMBOS: recarga UNA vez (guard de 10s en sessionStorage, igual que vite:preloadError)
 * para tomar el bundle nuevo; si tras recargar SIGUE fallando (raro), en vez de re-lanzar
 * —que crashea toda la app en el ErrorBoundary y ensucia Sentry con ruido transitorio— rendea
 * un prompt de recarga SOLO en ese slot.
 */
function lazyRetry<T extends ComponentType<unknown>>(factory: () => Promise<{ default: T }>) {
  const recover = (): { default: T } => {
    const KEY = 'chunk_reload_at';
    const last = Number(sessionStorage.getItem(KEY) || 0);
    if (Date.now() - last > 10000) {
      sessionStorage.setItem(KEY, String(Date.now()));
      window.location.reload();
      // Devuelve un componente vacío mientras la página recarga (no re-lanza → sin Sentry).
      return { default: (() => null) as unknown as T };
    }
    return { default: ChunkReloadPrompt as unknown as T };
  };
  return lazy(() =>
    factory()
      .then((mod) => (mod && typeof mod.default !== 'undefined' ? mod : recover()))
      .catch(() => recover()),
  );
}

// Páginas pesadas / poco frecuentes: se cargan bajo demanda (code-splitting) para aligerar
// la carga inicial. La navegación muestra un spinner breve mientras baja el chunk.
const ClaimDetail = lazyRetry(() => import('./pages/ClaimDetail').then((m) => ({ default: m.ClaimDetail })));
const RiskBoard = lazyRetry(() => import('./pages/RiskBoard'));
const HoUpdatesQueue = lazyRetry(() => import('./pages/HoUpdatesQueue').then((m) => ({ default: m.HoUpdatesQueue })));
const Reports = lazyRetry(() => import('./pages/Reports').then((m) => ({ default: m.Reports })));
const Settings = lazyRetry(() => import('./pages/Settings').then((m) => ({ default: m.Settings })));
const EmailHealth = lazyRetry(() => import('./pages/EmailHealth').then((m) => ({ default: m.EmailHealth })));
const Requests = lazyRetry(() => import('./pages/Requests').then((m) => ({ default: m.Requests })));
const CallMonitoring = lazyRetry(() => import('./pages/CallMonitoring').then((m) => ({ default: m.CallMonitoring })));
const Recordings = lazyRetry(() => import('./pages/Recordings').then((m) => ({ default: m.Recordings })));
// Training (mig 0472): videos y lecturas por departamento + la iniciación.
const Training = lazyRetry(() => import('./pages/Training').then((m) => ({ default: m.Training })));
// Proactive AI (mig 0473): asistentes por OpenRouter para los empleados + el engranaje de súper acceso.
const AiAssistants = lazyRetry(() => import('./pages/AiAssistants').then((m) => ({ default: m.AiAssistants })));
const AiChatPage = lazyRetry(() => import('./pages/AiAssistants').then((m) => ({ default: m.AiChatPage })));
const AiSettings = lazyRetry(() => import('./pages/AiSettings').then((m) => ({ default: m.AiSettings })));
const Complaints = lazyRetry(() => import('./pages/Complaints').then((m) => ({ default: m.Complaints })));
const Kudos = lazyRetry(() => import('./pages/Kudos').then((m) => ({ default: m.Kudos })));
const EmployeeEvaluation = lazyRetry(() => import('./pages/EmployeeEvaluation').then((m) => ({ default: m.EmployeeEvaluation })));
// PÁGINAS SECUNDARIAS FUERA DEL ARRANQUE (presupuesto 2026-10-07): el chunk de entrada tocó el tope de
// 480 KB gzip. Estas se abren a demanda —directorios, company board, efectividad, depreciación, diario de
// pagos, owed to us, finder, request payment auth, last viewed— y no tienen por qué bajar con el login.
const Finder = lazyRetry(() => import('./pages/Finder').then((m) => ({ default: m.Finder })));
const PaymentDiary = lazyRetry(() => import('./pages/PaymentDiary').then((m) => ({ default: m.PaymentDiary })));
const RequestPaymentAuth = lazyRetry(() => import('./pages/RequestPaymentAuth').then((m) => ({ default: m.RequestPaymentAuth })));
const OwedToUs = lazyRetry(() => import('./pages/OwedToUs').then((m) => ({ default: m.OwedToUs })));
const LastViewed = lazyRetry(() => import('./pages/LastViewed').then((m) => ({ default: m.LastViewed })));
const CompanyBoard = lazyRetry(() => import('./pages/CompanyBoard').then((m) => ({ default: m.CompanyBoard })));
const Effectiveness = lazyRetry(() => import('./pages/Effectiveness').then((m) => ({ default: m.Effectiveness })));
const Depreciation = lazyRetry(() => import('./pages/Depreciation').then((m) => ({ default: m.Depreciation })));
// CompanyDashboard recibe props (scopeMe/afterFilters) cuando lo embebe el dashboard de Claims; la ruta /company lo pinta pelado.
// EL MÓDULO SE DESREFERENCIA ACÁ, NO DENTRO DEL COMPONENTE (Sentry 2026-10-13 14:52 UTC en /company:
// "Cannot read properties of undefined (reading 'CompanyDashboard')", pantalla en blanco por el
// ErrorBoundary). Con `default: () => <m.CompanyDashboard />` el `m` se leía RECIÉN AL PINTAR: si el
// import de un chunk viejo resuelve `undefined`, ese objeto `{ default: fn }` es válido y la guarda de
// lazyRetry lo da por bueno, así que el crash salía después y sin recuperación. Leyéndolo acá, un `m`
// undefined tira DENTRO del factory y cae en el `.catch(() => recover())`, que es para lo que existe.
// Era la única de las 87 páginas con ese patrón; el resto hace `default: m.Algo`, que ya desreferencia.
const CompanyDashboard = lazyRetry(() => import('./pages/CompanyDashboard').then((m) => {
  const Pagina = m.CompanyDashboard;
  return { default: () => <Pagina /> };
}));
const InsuranceDirectory = lazyRetry(() => import('./pages/Directory').then((m) => ({ default: m.InsuranceDirectory })));
const ContractorsDirectory = lazyRetry(() => import('./pages/Directory').then((m) => ({ default: m.ContractorsDirectory })));
const EmployeeProfile = lazyRetry(() => import('./pages/EmployeeProfile').then((m) => ({ default: m.EmployeeProfile })));
const MyHours = lazyRetry(() => import('./pages/MyHours'));
const TimeSheet = lazyRetry(() => import('./pages/TimeSheet'));
const Payroll = lazyRetry(() => import('./pages/Payroll'));
const Broadcast = lazyRetry(() => import('./pages/Broadcast'));
const PayQueue = lazyRetry(() => import('./pages/PayQueue').then((m) => ({ default: m.PayQueue })));
const ChecksReceived = lazyRetry(() => import('./pages/ChecksReceived').then((m) => ({ default: m.ChecksReceived })));
// El cheque que SALE. Lazy como todo lo nuevo: el bundle de arranque tiene techo de 480 KB gzip
// y el CI lo mide (checkBundle.mjs).
const ChecksIssued = lazyRetry(() => import('./pages/ChecksIssued'));
const DepartmentInbox = lazyRetry(() => import('./pages/DepartmentInbox').then((m) => ({ default: m.DepartmentInbox })));
const SmsInbox = lazyRetry(() => import('./pages/SmsInbox').then((m) => ({ default: m.SmsInbox })));
// Hub móvil de comunicación (tab Inbox de la tab bar): Messaging + buzones + SMS en una pantalla.
const MobileInboxHub = lazyRetry(() => import('./pages/MobileInboxHub').then((m) => ({ default: m.MobileInboxHub })));
// Dashboards por departamento (2026-08-09).
const ManagementDashboard = lazyRetry(() => import('./pages/ManagementDashboard').then((m) => ({ default: m.ManagementDashboard })));
const SuperBoard = lazyRetry(() => import('./pages/SuperBoard').then((m) => ({ default: m.SuperBoard })));
// Templates (dueño 2026-10-28, mig 0553): todas las plantillas de email y SMS, por departamento.
const Templates = lazyRetry(() => import('./pages/Templates').then((m) => ({ default: m.Templates })));
// Agents (dueño 2026-11-01, mig 0562): el portal de los bots.
const Agents = lazyRetry(() => import('./pages/Agents').then((m) => ({ default: m.Agents })));
// Marketing (dueño 2026-11-02, mig 0563): alianzas con contratistas.
const Marketing = lazyRetry(() => import('./pages/Marketing').then((m) => ({ default: m.Marketing })));
const TvWall = lazyRetry(() => import('./pages/TvBoard').then((m) => ({ default: m.TvWall })));
const OfficePulse = lazyRetry(() => import('./pages/TvBoard').then((m) => ({ default: m.OfficePulse })));
const IntakeDashboard = lazyRetry(() => import('./pages/IntakeDashboard').then((m) => ({ default: m.IntakeDashboard })));
const ClaimsDeptDashboard = lazyRetry(() => import('./pages/ClaimsDeptDashboard').then((m) => ({ default: m.ClaimsDeptDashboard })));
const Contracts = lazyRetry(() => import('./pages/Contracts').then((m) => ({ default: m.Contracts })));
const SignContract = lazyRetry(() => import('./pages/SignContract').then((m) => ({ default: m.SignContract })));
const IntakeView = lazyRetry(() => import('./pages/IntakeView').then((m) => ({ default: m.IntakeView })));
const IntakeFormBuilder = lazyRetry(() => import('./pages/IntakeFormBuilder').then((m) => ({ default: m.IntakeFormBuilder })));
const Teams = lazyRetry(() => import('./pages/Teams').then((m) => ({ default: m.Teams })));
const PublicIntake = lazyRetry(() => import('./pages/PublicIntake').then((m) => ({ default: m.PublicIntake })));
const IntakeReview = lazyRetry(() => import('./pages/IntakeReview').then((m) => ({ default: m.IntakeReview })));
const IntakeFinder = lazyRetry(() => import('./pages/IntakeFinder').then((m) => ({ default: m.IntakeFinder })));
const NewClaims = lazyRetry(() => import('./pages/NewClaims').then((m) => ({ default: m.NewClaims })));
const SystemHealth = lazyRetry(() => import('./pages/SystemHealth').then((m) => ({ default: m.SystemHealth })));
const SchedulingDashboard = lazyRetry(() => import('./pages/SchedulingDashboard').then((m) => ({ default: m.SchedulingDashboard })));
const SchedulingUploads = lazyRetry(() => import('./pages/SchedulingUploads').then((m) => ({ default: m.SchedulingUploads })));
const SchedulingReports = lazyRetry(() => import('./pages/SchedulingReports').then((m) => ({ default: m.SchedulingReports })));
const SchedulingScheduled = lazyRetry(() => import('./pages/SchedulingScheduled').then((m) => ({ default: m.SchedulingScheduled })));
const Messaging = lazyRetry(() => import('./pages/Messaging').then((m) => ({ default: m.Messaging })));
const Notifications = lazyRetry(() => import('./pages/Notifications'));
const ContractorInbox = lazyRetry(() => import('./pages/ContractorInbox').then((m) => ({ default: m.ContractorInbox })));
const ClientPortalView = lazyRetry(() => import('./pages/ClientPortalView').then((m) => ({ default: m.ClientPortalView })));
const InspectorMirror = lazyRetry(() => import('./pages/InspectorMirror').then((m) => ({ default: m.InspectorMirror })));
const Concerns = lazyRetry(() => import('./pages/Concerns').then((m) => ({ default: m.Concerns })));
const ContractorPhotos = lazyRetry(() => import('./pages/ContractorPhotos').then((m) => ({ default: m.ContractorPhotos })));
const JobFlowUsers = lazyRetry(() => import('./pages/JobFlowUsers').then((m) => ({ default: m.JobFlowUsers })));
const SchedulingActivity = lazyRetry(() => import('./pages/SchedulingActivity').then((m) => ({ default: m.SchedulingActivity })));
const SchedulingCalendar = lazyRetry(() => import('./pages/SchedulingCalendar').then((m) => ({ default: m.SchedulingCalendar })));
const SchedulingTimeOff = lazyRetry(() => import('./pages/SchedulingTimeOff').then((m) => ({ default: m.SchedulingTimeOff })));
const Supplements = lazyRetry(() => import('./pages/Supplements').then((m) => ({ default: m.Supplements })));
const SupplementsDashboard = lazyRetry(() => import('./pages/SupplementsDashboard').then((m) => ({ default: m.SupplementsDashboard })));
const PriceLists = lazyRetry(() => import('./pages/PriceLists').then((m) => ({ default: m.PriceLists })));
// La Estimating Tool (2026-10-19): el estimado se arma acá, como en Xactimate; chunk aparte (tabla, picker de fotos, wizard).
const EstimatingTool = lazyRetry(() => import('./pages/EstimatingTool').then((m) => ({ default: m.EstimatingTool })));
const EstimatingHub = lazyRetry(() => import('./pages/EstimatingHub').then((m) => ({ default: m.EstimatingHub })));
const VerifiedMeasurements = lazyRetry(() => import('./pages/VerifiedMeasurements').then((m) => ({ default: m.VerifiedMeasurements })));
// CLAIMCAM (dueño 2026-10-10): la app de fotos propia. Todo lazy: la galería, el editor y la cámara no entran al arranque.
const ClaimCam = lazyRetry(() => import('./pages/ClaimCam').then((m) => ({ default: m.ClaimCam })));
const ClaimCamProject = lazyRetry(() => import('./pages/ClaimCamProject').then((m) => ({ default: m.ClaimCamProject })));
const ClaimCamReport = lazyRetry(() => import('./pages/ClaimCamReport').then((m) => ({ default: m.ClaimCamReport })));
const ClaimCamCapture = lazyRetry(() => import('./pages/ClaimCamCapture').then((m) => ({ default: m.ClaimCamCapture })));
const PublicClaimCamCapture = lazyRetry(() => import('./pages/PublicClaimCamCapture').then((m) => ({ default: m.PublicClaimCamCapture })));
const PublicClaimCamShare = lazyRetry(() => import('./pages/PublicClaimCamShare').then((m) => ({ default: m.PublicClaimCamShare })));
const PublicClaimCamHome = lazyRetry(() => import('./pages/PublicClaimCamHome').then((m) => ({ default: m.PublicClaimCamHome })));
const Archive = lazyRetry(() => import('./pages/Archive').then((m) => ({ default: m.Archive })));
const Inspectors = lazyRetry(() => import('./pages/Inspectors').then((m) => ({ default: m.Inspectors })));
const InspectorHistory = lazyRetry(() => import('./pages/InspectorHistory').then((m) => ({ default: m.InspectorHistory })));
const InspectorHome = lazyRetry(() => import('./pages/InspectorHome').then((m) => ({ default: m.InspectorHome })));
const CoverageDetector = lazyRetry(() => import('./pages/CoverageDetector').then((m) => ({ default: m.CoverageDetector })));
// Ventana propia del share screen (segundo monitor): sin shell, hereda cookie, tema e idioma.
const ShareViewWindow = lazyRetry(() => import('./components/share/ShareViewWindow').then((m) => ({ default: m.ShareViewWindow })));
// Record (spec §4.12): la grabación en su propia ventana (sin shell) y la ventana de control popup.
const RecView = lazyRetry(() => import('./pages/RecView').then((m) => ({ default: m.RecView })));
const RecControlWindow = lazyRetry(() => import('./pages/RecControlWindow').then((m) => ({ default: m.RecControlWindow })));

/** Registra un pageview (path sanitizado) en cada cambio de ruta. No-op sin PostHog. */
function RouteTracker() {
  const loc = useLocation();
  useEffect(() => {
    trackPageview(loc.pathname);
  }, [loc.pathname]);
  return null;
}

export function App() {
  return (
    <>
    <RouteTracker />
    {/* Memoria de scroll por URL: al dar "atrás" vuelves EXACTAMENTE donde ibas en la lista. */}
    <ScrollMemory />
    <Suspense fallback={<div style={{ padding: 48, textAlign: 'center', color: 'var(--muted)', fontSize: 'var(--fs-sm)' }}>…</div>}>
    {/* Share screen (spec §4.11): el provider envuelve TODAS las rutas para servir al shell de oficina
        y a la app del inspector (/inspector). Sin sesión (login, plataforma) queda inerte; en
        /share/view/:id se apaga solo para no pelear con el provider propio de esa ventana. */}
    <ShareProvider>
    {/* Record (spec §4.12): grabar un video tipo Loom. Mismo lugar que el ShareProvider: la píldora
        REC, la vista previa y las tarjetas se ven desde cualquier pantalla. */}
    <RecProvider>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/platform/login" element={<PlatformLogin />} />
      <Route path="/platform" element={<PlatformDashboard />} />
      <Route path="/intake/view/:token" element={<IntakeView />} />
      <Route path="/intake/:token" element={<PublicIntake />} />
      <Route path="/sign/:token" element={<SignContract />} />
      <Route path="/share/view/:id" element={<ShareViewWindow />} />
      {/* Record: el player en su ventana (también en el teléfono del inspector) y el control popup. */}
      <Route path="/rec/control" element={<RecControlWindow />} />
      <Route path="/rec/:id" element={<RecView />} />
      {/* Efectividad compartida con un cliente: sin login y fuera del shell del app. */}
      <Route path="/p/effectiveness/:token" element={<PublicEffectiveness />} />
      {/* El update semanal que abre el homeowner desde el SMS o el correo: sin login, siempre en claro. */}
      <Route path="/p/update/:token" element={<PublicHoUpdate />} />
      <Route path="/r/:token" element={<PublicReview />} />
      {/* El video del ATR que abre el adjuster desde el email: sin login, con la marca y el nombre que le puso la oficina. */}
      <Route path="/p/atr/:token" element={<PublicAtrVideo />} />
      <Route path="/p/photos/upload/:token" element={<PublicClaimCamCapture />} />
      <Route path="/p/photos/:token" element={<PublicClaimCamShare />} />
      <Route path="/p/claimcam/:token" element={<PublicClaimCamHome />} />
      <Route path="/p/claimcam/:token/:claimId" element={<PublicClaimCamHome />} />
      {/* App de campo del inspector — standalone, sin el AppShell de oficina. */}
      <Route path="/inspector" element={<InspectorHome />} />
      {/* CLAIMCAM: la cámara del app de campo (pantalla completa, fuera del shell). */}
      <Route path="/claimcam/capture" element={<ClaimCamCapture />} />
      <Route path="/claimcam/capture/:claimId" element={<ClaimCamCapture />} />
      {/* El board de los televisores de la oficina — standalone, sin shell, siempre en modo TV. */}
      <Route path="/tv" element={<TvWall />} />

      <Route element={<ShellOTv />}>
        <Route path="/" element={<HomeRouter />} />
        {/* El dashboard de Payments sigue accesible directo (el landing redirige por depto). */}
        <Route path="/payments" element={<Dashboard />} />
        <Route path="/management" element={<ManagementDashboard />} />
        <Route path="/super-board" element={<SuperBoard />} />
        {/* La misma pared, dentro del app y con nombres completos: cualquiera de oficina la abre y va al claim. */}
        <Route path="/office-pulse" element={<OfficePulse />} />
        <Route path="/templates" element={<Templates />} />
        <Route path="/agents" element={<Agents />} />
        <Route path="/marketing" element={<Marketing />} />
        <Route path="/dept/intake/dashboard" element={<IntakeDashboard />} />
        <Route path="/dept/claims/dashboard" element={<ClaimsDeptDashboard />} />
        <Route path="/finder" element={<Finder />} />
        <Route path="/diary" element={<PaymentDiary />} />
        <Route path="/last-viewed" element={<LastViewed />} />
        <Route path="/claims" element={<Claims />} />
        <Route path="/claims/:id" element={<ClaimDetail />} />
        <Route path="/claims/:id/estimate" element={<EstimatingTool />} />
        <Route path="/claims/:id/estimate/:draftId" element={<EstimatingTool />} />
        <Route path="/risk" element={<RiskBoard />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/requests" element={<Requests />} />
        <Route path="/call-monitoring" element={<CallMonitoring />} />
        <Route path="/recordings" element={<Recordings />} />
        <Route path="/training" element={<Training />} />
        <Route path="/ai" element={<AiAssistants />} />
        <Route path="/ai/settings" element={<AiSettings />} />
        <Route path="/ai/:assistantId" element={<AiChatPage />} />
        <Route path="/ai/:assistantId/c/:conversationId" element={<AiChatPage />} />
        <Route path="/complaints" element={<Complaints />} />
        <Route path="/kudos" element={<Kudos />} />
        <Route path="/evaluation" element={<EmployeeEvaluation />} />
        <Route path="/employee/:id" element={<EmployeeProfile />} />
        <Route path="/my-hours" element={<MyHours />} />
        <Route path="/timesheet" element={<TimeSheet />} />
        <Route path="/payroll" element={<Payroll />} />
        <Route path="/broadcast" element={<Broadcast />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/email-health" element={<EmailHealth />} />
        <Route path="/mortgage" element={<Mortgage />} />
        <Route path="/review" element={<ReviewInvoice />} />
        <Route path="/checks" element={<ChecksReceived />} />
        <Route path="/checks-issued" element={<ChecksIssued />} />
        <Route path="/queue/:key" element={<QueuePage />} />
        <Route path="/waiting-to-clear" element={<WaitingToClear />} />
        <Route path="/ready-to-pay" element={<ReadyToPay />} />
        <Route path="/homeowner-with-check" element={<HeldChecks who="homeowner" />} />
        <Route path="/contractor-with-check" element={<HeldChecks who="contractor" />} />
        <Route path="/bounced-checks" element={<BouncedChecks />} />
        <Route path="/pay-queue" element={<PayQueue />} />
        <Route path="/request-payment-auth" element={<RequestPaymentAuth />} />
        <Route path="/owed-to-us" element={<OwedToUs />} />
        <Route path="/invoice-todo" element={<InvoiceTodo />} />
        <Route path="/waiting-clear" element={<WaitingClear />} />
        {/* Framework multi-departamento (aditivo). Payments conserva sus rutas propias. */}
        <Route path="/company" element={<CompanyDashboard />} />
        <Route path="/dept/scheduling" element={<SchedulingDashboard />} />
        <Route path="/dept/scheduling/dashboard" element={<SchedulingDashboard />} />
        <Route path="/dept/scheduling/scheduled" element={<SchedulingScheduled />} />
        <Route path="/dept/scheduling/calendar" element={<SchedulingCalendar />} />
        <Route path="/dept/scheduling/time-off" element={<SchedulingTimeOff />} />
        <Route path="/dept/scheduling/activity" element={<SchedulingActivity />} />
        <Route path="/dept/scheduling/uploads" element={<SchedulingUploads />} />
        <Route path="/dept/scheduling/reports" element={<SchedulingReports />} />
        <Route path="/dept/scheduling/inspector-history" element={<InspectorHistory />} />
        <Route path="/messaging" element={<Messaging />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/contractor-inbox" element={<ContractorInbox />} />
        {/* Client Portal del staff: buscador por nombre del HO + la vista del cliente en un iframe. */}
        <Route path="/client-portal" element={<ClientPortalView />} />
        {/* ClaimRoute: la oficina ve la app de un inspector tal como él la ve (solo lectura). */}
        <Route path="/claimroute" element={<InspectorMirror />} />
        <Route path="/concerns" element={<Concerns />} />
        <Route path="/contractor-photos" element={<ContractorPhotos />} />
        <Route path="/jobflow" element={<JobFlowUsers />} />
        <Route path="/dept/supplements" element={<Supplements />} />
        <Route path="/dept/supplements/dashboard" element={<SupplementsDashboard />} />
        <Route path="/dept/supplements/queue/:status" element={<Supplements />} />
        <Route path="/dept/supplements/price-lists" element={<PriceLists />} />
        {/* Estimating (dueño 2026-10-19): buscar un cliente y estimar; por hacer, en curso y hechos. */}
        <Route path="/dept/supplements/estimating" element={<EstimatingHub />} />
        <Route path="/dept/supplements/verified-measurements" element={<VerifiedMeasurements />} />
        <Route path="/claimcam" element={<ClaimCam />} />
        <Route path="/claimcam/:claimId" element={<ClaimCamProject />} />
        <Route path="/claimcam/:claimId/reports/:reportId" element={<ClaimCamReport />} />
        {/* Estimators fue un departamento aparte entre el 2026-08-17 y el 2026-08-27 (migs 0374 y
            0416). Los links viejos —bookmarks, notificaciones, correos internos— siguen vivos, así
            que las dos rutas redirigen al board unificado en vez de dar 404. */}
        <Route path="/dept/estimators" element={<Navigate to="/dept/supplements" replace />} />
        <Route path="/dept/estimators/queue/:status" element={<RedirEstimatorQueue />} />
        <Route path="/archive" element={<Archive />} />
        <Route path="/company-board" element={<CompanyBoard />} />
        <Route path="/effectiveness" element={<Effectiveness />} />
        <Route path="/ho-updates" element={<HoUpdatesQueue />} />
        <Route path="/depreciation" element={<Depreciation />} />
        <Route path="/dept/:key" element={<DepartmentHome />} />
        <Route path="/dept/:key/inbox" element={<DepartmentInbox />} />
        <Route path="/sms" element={<SmsInbox />} />
        {/* Hub de Inbox móvil (mockup B-mobile phone 3): destino del tab Inbox de la tab bar. */}
        <Route path="/inbox" element={<MobileInboxHub />} />
        <Route path="/dept/:key/queue/:status" element={<DepartmentQueue />} />
        <Route path="/insurance" element={<InsuranceDirectory />} />
        <Route path="/contractors" element={<ContractorsDirectory />} />
        <Route path="/contracts" element={<Contracts />} />
        <Route path="/intake-form" element={<IntakeFormBuilder />} />
        <Route path="/teams" element={<Teams />} />
        <Route path="/inspectors" element={<Inspectors />} />
        <Route path="/coverage-detector" element={<CoverageDetector />} />
        <Route path="/intake-review" element={<IntakeReview />} />
        <Route path="/intake-finder" element={<IntakeFinder />} />
        <Route path="/new-claims" element={<NewClaims />} />
        <Route path="/system-health" element={<SystemHealth />} />
      </Route>
    </Routes>
    </RecProvider>
    </ShareProvider>
    </Suspense>
    </>
  );
}
